import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useState, useEffect, useRef } from 'react';
import PropTypes__default from 'prop-types';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import styles from './accordion.scss.js';
import { Injecss } from '../../packages/injecss/injecss.js';

var bem = HoneyBem.init('accordion', styles);
var Accordion = function (_a) {
  var children = _a.children,
    extraClasses = _a.extraClasses,
    open = _a.open,
    onToggle = _a.onToggle,
    preserveChildrenWhenClosed = _a.preserveChildrenWhenClosed,
    title = _a.title,
    rest = __rest(_a, ["children", "extraClasses", "open", "onToggle", "preserveChildrenWhenClosed", "title"]);
  var _b = useState(open),
    isOpen = _b[0],
    setIsOpen = _b[1];
  useEffect(function () {
    setIsOpen(open);
  }, [open]);
  var detailsRef = useRef(null);
  var summaryRef = useRef(null);
  var accordionClassNames = bem.b(false, extraClasses);
  /**
   * emits toggle event on a given element if needed
   * acts as polyfill, kind of mimics native browser behavior for details HTML element
   * @param {HTMLElement} element - element to trigger even on
   */
  var triggerToggle = function (element) {
    var event = new CustomEvent('toggle');
    element.dispatchEvent(event);
  };
  /**
   * toggles "open" attribute on a details element
   * also emits "toggle" event
   * acts as a polyfill for browsers that don't support <details> element
   */
  var toggleAccordionOpen = function () {
    var detailsElem = detailsRef.current;
    // check if browser has native support for details element, open attribute needs to be supported
    if (detailsElem && typeof detailsElem.open !== 'boolean') {
      if (!detailsElem.hasAttribute('open')) {
        detailsElem.setAttribute('open', '');
      } else {
        detailsElem.removeAttribute('open');
      }
      triggerToggle(detailsElem);
    }
  };
  /**
   * handles keyboard events, to toggle element state
   * acts as a polyfill for browsers that don't support <details> element
   * @param {KeyboardEvent} event - event object
   */
  var handleKeyDown = function (event) {
    if (event.key === ' ' || event.key === 'Enter') {
      toggleAccordionOpen();
    }
  };
  /**
   * handles toggle event, runs custom callback if present
   * @param {object} event - event object
   */
  var handleToggle = function (event) {
    if (detailsRef && detailsRef.current) {
      setIsOpen(detailsRef.current.open);
      if (onToggle && typeof onToggle === 'function') {
        onToggle(event, detailsRef.current.open);
      }
    }
  };
  return /*#__PURE__*/React__default__default.createElement("details", __assign({
    ref: detailsRef,
    className: accordionClassNames,
    open: isOpen,
    onToggle: function (event) {
      handleToggle(event);
    }
  }, rest), /*#__PURE__*/React__default__default.createElement("summary", {
    tabIndex: 0,
    role: "button",
    "aria-expanded": isOpen,
    className: bem.e('title'),
    onClick: toggleAccordionOpen,
    onKeyDown: function (event) {
      handleKeyDown(event);
    },
    ref: summaryRef
  }, title), /*#__PURE__*/React__default__default.createElement("article", {
    className: bem.e('content')
  }, (preserveChildrenWhenClosed || isOpen) && children));
};
Accordion.displayName = 'Accordion';
Accordion.defaultProps = {
  extraClasses: '',
  onToggle: function () {
    return null;
  },
  open: false,
  preserveChildrenWhenClosed: true
};
Accordion.propTypes = {
  children: PropTypes__default.node.isRequired,
  extraClasses: PropTypes__default.string,
  onToggle: PropTypes__default.func,
  open: PropTypes__default.bool,
  preserveChildrenWhenClosed: PropTypes__default.bool,
  title: PropTypes__default.node.isRequired
};
var accordion = Injecss(Accordion, styles);

export { Accordion, accordion as default };
